import { Box, Button, Popper, Tab, Tabs } from "@mui/material";
import { motion, useScroll } from "framer-motion";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { Link, useLocation } from "react-router-dom";
import Footer from "../../components/Footer";
import CompactCounterbalanceTrucks from "./compactCounterbalanceTrucks";
import ContactSection from "./contactSection";
import CounterbalanceTrucks from "./counterbalanceTrucks";
import ElectricCrossoverForklifts from "./electricCrossoverForklifts";
import ElectricForklifts from "./electricForklifts";
import ElectricStackerTrucks from "./electricStackerTrucks";
import ElectricTowTractors from "./electricTowTractors";
import OrderPickers from "./orderPickers";
import PalletTrucks from "./palletTrucks";
import ReachTrucks from "./reachTrucks";

// Define tabs with their labels and slug values.
const tabsData = [
  { label: "Čelní spalovací VZV", slug: "celni-spalovaci-vzv" },
  { label: "Elektrické Crossover VZV", slug: "elektricke-crossover-vzv", newLabel: true },
  { label: "Kompaktní čelní VZV", slug: "kompaktni-celni-vzv" },
  { label: "Čelní elektrické VZV", slug: "celni-elektricke-vzv" },
  { label: "Elektrické zakladače", slug: "elektricke-zakladace" },
  { label: "Paletové vozíky", slug: "paletove-voziky" },
  { label: "Retrak", slug: "retrak" },
  { label: "Vychystávací vozíky", slug: "vychystavaci-voziky" },
  { label: "Elektrické tahače", slug: "elektricke-tahace" },
];

const ClarkOverview = () => {
  const location = useLocation();
  const anchorRefs = useRef({});
  const tabsRef = useRef(null);

  // State to track which “Novinky” tabs are fully visible
  const [visibleNovinky, setVisibleNovinky] = useState(new Set());

  // --- URL ↔ tab sync ---
  const params = new URLSearchParams(location.search);
  const initialSlug = params.get("tab") || tabsData[0].slug;
  const [selectedTab, setSelectedTab] = useState(initialSlug);

  useEffect(() => {
    const slug = new URLSearchParams(location.search).get("tab");
    if (slug && slug !== selectedTab) {
      setSelectedTab(slug);
    }
  }, [location.search, selectedTab]);

  // Measure in-page tabs offset and track scrollY
  const [tabsBottomY, setTabsBottomY] = useState(0);
  const { scrollY } = useScroll();
  const [isPastTabs, setIsPastTabs] = useState(false);

  // On mount, measure the distance of the tabs from top
  useLayoutEffect(() => {
    if (tabsRef.current) {
      const rect = tabsRef.current.getBoundingClientRect();
      setTabsBottomY(rect.top + window.scrollY + rect.height);
    }
  }, []);

  // Auto-scroll při inicializaci, pokud je v URL param “tab”
  useEffect(() => {
    const tabSlug = new URLSearchParams(location.search).get("tab");
    if (tabSlug && tabsBottomY > 0 && tabsRef.current) {
      const rect = tabsRef.current.getBoundingClientRect();
      window.scrollTo({ top: window.scrollY + rect.top - 64 - 8, behavior: "smooth" });
    }
  }, [tabsBottomY, location.search]);

  // Sticky-tabs trigger
  useEffect(() => {
    return scrollY.onChange((y) => {
      if (tabsBottomY > 0) {
        setIsPastTabs(y > tabsBottomY - 64);
      }
    });
  }, [scrollY, tabsBottomY]);

  // Scroll-into-view on tab change
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (tabsRef.current) {
      const rect = tabsRef.current.getBoundingClientRect();
      window.scrollTo({ top: window.scrollY + rect.top - 64 - 8, behavior: "smooth" });
    }
  }, [selectedTab]);

  // Calculate which tabs are fully visible
  useEffect(() => {
    if (!tabsRef.current) return;
    const scroller = tabsRef.current.querySelector(".MuiTabs-scroller");
    if (!scroller) return;

    const calculate = () => {
      const { scrollLeft, clientWidth } = scroller;
      const newSet = new Set();

      tabsData.forEach((tab) => {
        if (!tab.newLabel) return;
        const el = anchorRefs.current[tab.slug];
        if (!el) return;

        const left = el.offsetLeft;
        const right = left + el.offsetWidth;
        const viewportLeft = scrollLeft;
        const viewportRight = scrollLeft + clientWidth;

        // průnik intervalu [left, right] s [viewportLeft, viewportRight]
        const overlap = Math.min(right, viewportRight) - Math.max(left, viewportLeft);

        // pokud je overlap alespoň 70% šířky el
        if (overlap > 0 && overlap >= 0.7 * el.offsetWidth) {
          newSet.add(tab.slug);
        }
      });

      setVisibleNovinky(newSet);
    };

    calculate();
    scroller.addEventListener("scroll", calculate);
    window.addEventListener("resize", calculate);
    return () => {
      scroller.removeEventListener("scroll", calculate);
      window.removeEventListener("resize", calculate);
    };
  }, []);

  const stats = [
    { end: 110, suffix: " let", label: "Více než století inovací a spolehlivosti" },
    { end: 1_000_000, suffix: "+ ks", label: "Více než milion vozíků Clark v provozu po celém světě" },
    { end: 8_000, suffix: " kg", label: "Maximální nosnost vozíků Clark pro nejtěžší provozy" },
    { end: 10, suffix: "+ let", label: "Životnost strojů při správné údržbě" },
  ];

  const renderTabContent = () => {
    switch (selectedTab) {
      case "celni-spalovaci-vzv":
        return (
          <div className="min-h-screen">
            <CounterbalanceTrucks />
          </div>
        );
      case "elektricke-crossover-vzv":
        return (
          <div className="min-h-screen">
            <ElectricCrossoverForklifts />
          </div>
        );
      case "kompaktni-celni-vzv":
        return (
          <div className="min-h-screen">
            <CompactCounterbalanceTrucks />
          </div>
        );
      case "celni-elektricke-vzv":
        return (
          <div className="min-h-screen">
            <ElectricForklifts />
          </div>
        );
      case "elektricke-zakladace":
        return (
          <div className="min-h-screen">
            <ElectricStackerTrucks />
          </div>
        );
      case "paletove-voziky":
        return (
          <div className="min-h-screen">
            <PalletTrucks />
          </div>
        );
      case "retrak":
        return (
          <div className="min-h-screen">
            <ReachTrucks />
          </div>
        );
      case "vychystavaci-voziky":
        return (
          <div className="min-h-screen">
            <OrderPickers />
          </div>
        );
      case "elektricke-tahace":
        return (
          <div className="min-h-screen">
            <ElectricTowTractors />
          </div>
        );
      default:
        return null;
    }
  };

  const scrollToTabs = () => {
    if (tabsRef.current) {
      const rect = tabsRef.current.getBoundingClientRect();
      window.scrollTo({ top: window.scrollY + rect.top - 64 - 8, behavior: "smooth" });
    }
  };
  const scrollToContact = () => {
    const el = document.getElementById("contactSection");
    if (el) {
      const rect = el.getBoundingClientRect();
      window.scrollTo({ top: window.scrollY + rect.top - 128 - 8, behavior: "smooth" });
    } else {
      console.warn("Kontaktni sekce s id 'contactSection' nenalezena");
    }
  };

  return (
    <div className="relative overflow-hidden">
      <section className="relative flex min-h-[60vh] lg:min-h-[calc(100vh-4rem)] px-3 pt-20 sm:pt-0 lg:px-8 xl:px-20 gap-x-6 justify-center items-center lg:flex-row flex-col">
        <div className="w-full">
          <h1 className="text-5xl">
            Technika, která zvládne vaše <span className="text-primary">tempo.</span>
          </h1>
          <p className="mt-5 text-neutral-600">
            Prémiové vozíky Clark - síla, spolehlivost a výkon pro každý provoz, kde není prostor pro kompromisy. Vyberte si stroje, které zvládnou každodenní nasazení bez zbytečných výpadků a zvýší
            efektivitu vašeho podnikání.
          </p>
          <div className="flex flex-wrap gap-2 mt-8 sm:gap-3 sm:mt-5">
            <Button variant="contained" className="font-semibold normal-case rounded-full w-44 bg-gradient-to-r from-primaryLinearFrom to-primary" onClick={scrollToTabs}>
              Prohlédněte si stroje
            </Button>
            <Button className="font-semibold normal-case border border-solid rounded-full shadow-sm text-darkerPrimary w-44 bg-white-100" onClick={scrollToContact}>
              Spojte se s námi
            </Button>
          </div>
        </div>
        <div className="justify-end hidden w-full lg:flex">
          <div className="relative w-[25rem] p-5 h-[25rem] xl:w-[38rem] xl:h-[38rem] max-h-[calc(100vh-15rem)] xl:p-7 overflow-hidden rounded-lg border border-neutral-100 shadow-[0_0_15px_rgba(0,0,0,0.15)]">
            <img className="object-cover w-full h-full rounded-lg" src="/images/ClarkOverview/clark-v-akci.webp" alt="Obrázek vozíku Clark v akci" />
          </div>
        </div>
        <div className="absolute top-0 -z-10 -right-20 w-[300px] h-[300px] lg:w-[500px] lg:h-[500px] bg-primary rounded-full filter blur-[200px] opacity-30 animate-drift" />
        <div className="absolute bottom-0 -z-10 -left-20 w-[300px] h-[300px] lg:w-[400px] lg:h-[400px] bg-primary rounded-full filter blur-[150px] opacity-30 animate-drift" />
      </section>

      <section className="px-4 py-20 mx-auto max-w-[85rem]">
        <div className="grid grid-cols-1 gap-8 text-center sm:grid-cols-2 lg:grid-cols-4">
          {stats.map(({ end, suffix, label }, i) => (
            <div key={i} className="flex flex-col items-center">
              <CountUp start={0} end={end} duration={2.5} separator=" " enableScrollSpy scrollSpyOnce suffix={suffix} className="text-3xl font-bold sm:text-4xl md:text-5xl text-primary" />
              <span className="mt-2 text-sm text-neutral-600">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <hr className="h-px mb-5 border-0 bg-neutral-200" />

      <section className="relative min-h-screen px-3 pt-16 pb-32 lg:px-8 xl:px-20">
        <div ref={tabsRef} className="relative z-0 flex justify-center overflow-visible">
          <Tabs
            value={selectedTab}
            onChange={(_, newSlug) => setSelectedTab(newSlug)}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            className="px-1 py-1 mt-10 overflow-visible bg-white border border-solid rounded-full border-neutral-200"
            TabIndicatorProps={{
              style: {
                top: 0,
                bottom: "auto",
                height: "100%",
                borderRadius: "9999px",
                backgroundColor: "rgba(190, 215, 1, 0.1)",
                transition: "all 300ms ease",
                zIndex: 0,
              },
            }}
            sx={{
              overflowX: "hidden !important",
              overflowY: "visible !important",
              "& .MuiTabs-scrollableX": {
                overflowX: "hidden !important",
                overflowY: "visible !important",
              },
              "& .MuiTabs-scroller": {
                overflowX: "auto !important",
                overflowY: "visible !important",
              },
              "& .MuiTab-root": {
                overflow: "visible !important",
                position: "relative",
                zIndex: 10,
              },

              "& .MuiTabs-flexContainer": { gap: "15px", position: "relative", zIndex: 1 },
              "& .MuiTabs-scrollButtons.Mui-disabled": { width: 0 },
            }}
          >
            {tabsData.map((tab) => (
              <Tab
                key={tab.slug}
                value={tab.slug}
                component={Link}
                to={`?tab=${tab.slug}`}
                onClick={() => setSelectedTab(tab.slug)}
                className={`px-4 py-2 rounded-full normal-case ${selectedTab === tab.slug ? "text-darkerPrimary" : "text-neutral-700"}`}
                ref={(el) => {
                  anchorRefs.current[tab.slug] = el;
                }}
                label={tab.label}
                sx={{ overflow: "visible", position: "relative" }}
              />
            ))}
          </Tabs>

          {tabsData.map((tab) => {
            if (!tab.newLabel) return null;
            const anchorEl = anchorRefs.current[tab.slug];
            return (
              <Popper
                key={tab.slug}
                open={visibleNovinky.has(tab.slug)}
                anchorEl={anchorEl}
                placement="top"
                className="z-[1]"
                modifiers={[
                  { name: "offset", options: { offset: [0, -8] } },
                  { name: "flip", enabled: false },
                ]}
                style={{ zIndex: 1500 }}
              >
                <Box className=" bg-[#BED701] text-white text-[10px] font-bold uppercase rounded-full px-2 py-0.5 shadow-md pointer-events-none">Nové stroje</Box>
              </Popper>
            );
          })}
        </div>

        {/* Sticky clone under navbar */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={isPastTabs ? { y: 0, opacity: 1 } : { y: -50, opacity: 0 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ type: "tween", duration: 0.3 }}
          className="fixed left-0 right-0 top-[60px] lg:top-16 overflow-visible z-50 pointer-events-none"
        >
          <div className="w-full mx-auto lg:px-4 pointer-events-auto max-w-[90rem] lg:w-fit">
            <Tabs
              value={selectedTab}
              onChange={(_, slug) => setSelectedTab(slug)}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              className="w-full px-1 py-1 bg-white border border-solid rounded-none lg:w-auto lg:rounded-full border-neutral-200"
              TabIndicatorProps={{
                style: { top: 0, bottom: "auto", height: "100%", borderRadius: "9999px", backgroundColor: "rgba(190,215,1,0.1)", transition: "all 300ms ease", zIndex: 0 },
              }}
              sx={{
                overflowX: "hidden !important",
                overflowY: "visible !important",
                "& .MuiTabs-scrollableX": { overflowX: "hidden !important", overflowY: "visible !important" },
                "& .MuiTabs-scroller": { overflowX: "auto !important", overflowY: "visible !important" },
                "& .MuiTab-root": { overflow: "visible !important", position: "relative", zIndex: 10 },
                "& .MuiTabs-flexContainer": { gap: "15px", position: "relative", zIndex: 1 },
                "& .MuiTabs-scrollButtons.Mui-disabled": { width: 0 },
              }}
            >
              {tabsData.map((tab) => (
                <Tab
                  key={tab.slug}
                  value={tab.slug}
                  component={Link}
                  to={`?tab=${tab.slug}`}
                  onClick={() => setSelectedTab(tab.slug)}
                  label={
                    <span className="relative inline-block">
                      {tab.label}
                      {tab.newLabel && (
                        <span className="absolute -top-4 whitespace-nowrap translate-x-1/2 right-1/2 bg-[#BED701] text-white text-[10px] font-bold uppercase rounded-full px-2 py-0.5 shadow-md">
                          Nové stroje
                        </span>
                      )}
                    </span>
                  }
                  className={`px-4 py-2 rounded-full normal-case ${selectedTab === tab.slug ? "text-darkerPrimary" : "text-neutral-700"}`}
                  sx={{ overflow: "visible", position: "relative" }}
                />
              ))}
            </Tabs>
          </div>
        </motion.div>

        {renderTabContent()}
      </section>

      <ContactSection />

      <Footer />
    </div>
  );
};

export default ClarkOverview;
